<template>
  <div class="legal-page">
    <div class="legal-shell">
      <div class="legal-topbar">
        <router-link class="legal-back" to="/login">
          <i class="el-icon-arrow-left"></i>
          返回登录
        </router-link>
      </div>

      <main class="legal-panel">
        <div v-if="loading" class="legal-state">正在加载协议内容...</div>
        <div v-else-if="error" class="legal-state legal-state-error">
          {{ error }}
        </div>
        <article
          v-else
          class="legal-content"
          v-html="renderedMarkdown"
        ></article>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: "LegalMarkdown",
  data() {
    return {
      markdown: "",
      loading: true,
      error: "",
    };
  },
  computed: {
    renderedMarkdown() {
      return this.renderMarkdown(this.markdown);
    },
  },
  created() {
    this.loadMarkdown();
  },
  methods: {
    async loadMarkdown() {
      try {
        const baseUrl = process.env.BASE_URL || "/";
        const response = await fetch(`${baseUrl}legal/agreement-privacy.md`, {
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        this.markdown = await response.text();
      } catch (error) {
        this.error = "协议内容加载失败，请稍后重试。";
      } finally {
        this.loading = false;
      }
    },
    escapeHtml(value) {
      return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    renderInline(value) {
      const escaped = this.escapeHtml(value);

      return escaped
        .replace(
          /\[([^\]]+)\]\((https?:\/\/[^)\s"]+|mailto:[^)\s"]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
        )
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    },
    renderMarkdown(source) {
      const lines = source.replace(/\r\n/g, "\n").split("\n");
      const blocks = [];
      let paragraph = [];
      let listItems = [];
      let listTag = "";

      const flushParagraph = () => {
        if (!paragraph.length) return;
        blocks.push(`<p>${this.renderInline(paragraph.join(" "))}</p>`);
        paragraph = [];
      };

      const flushList = () => {
        if (!listItems.length) return;
        blocks.push(
          `<${listTag}>${listItems
            .map((item) => `<li>${this.renderInline(item)}</li>`)
            .join("")}</${listTag}>`
        );
        listItems = [];
        listTag = "";
      };

      lines.forEach((rawLine) => {
        const line = rawLine.trim();

        if (!line) {
          flushParagraph();
          flushList();
          return;
        }

        const heading = line.match(/^(#{1,6})\s+(.+)$/);
        if (heading) {
          flushParagraph();
          flushList();
          const level = heading[1].length;
          blocks.push(
            `<h${level}>${this.renderInline(heading[2])}</h${level}>`
          );
          return;
        }

        const unordered = line.match(/^[-*+]\s+(.+)$/);
        const ordered = line.match(/^\d+\.\s+(.+)$/);
        if (unordered || ordered) {
          flushParagraph();
          const nextTag = ordered ? "ol" : "ul";
          if (listTag && listTag !== nextTag) {
            flushList();
          }
          listTag = nextTag;
          listItems.push((unordered || ordered)[1]);
          return;
        }

        flushList();
        paragraph.push(line);
      });

      flushParagraph();
      flushList();

      return blocks.join("");
    },
  },
};
</script>

<style lang="scss">
.legal-page {
  min-height: 100vh;
  box-sizing: border-box;
  padding: 32px 16px 56px;
  background: #f5f7fb;
  color: #1f2d3d;
}

.legal-shell {
  max-width: 960px;
  margin: 0 auto;
}

.legal-topbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 36px;
  margin-bottom: 16px;
}

.legal-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #3572d8;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
}

.legal-back:hover {
  color: #1f56b3;
}

.legal-panel {
  min-height: 420px;
  box-sizing: border-box;
  padding: 48px 56px;
  border: 1px solid #e4e9f2;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(21, 42, 72, 0.08);
}

.legal-state {
  padding: 96px 0;
  color: #6b778c;
  font-size: 15px;
  text-align: center;
}

.legal-state-error {
  color: #c33b32;
}

.legal-content {
  color: #27364a;
  font-size: 15px;
  line-height: 1.9;
}

.legal-content h1 {
  margin: 0 0 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e8edf5;
  color: #142033;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.35;
}

.legal-content h2 {
  margin: 36px 0 18px;
  color: #172640;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.4;
}

.legal-content h3 {
  margin: 30px 0 12px;
  color: #1f2d3d;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
}

.legal-content p {
  margin: 0 0 14px;
  text-align: justify;
}

.legal-content ul,
.legal-content ol {
  margin: 0 0 16px;
  padding-left: 24px;
}

.legal-content li {
  margin-bottom: 8px;
}

.legal-content a {
  color: #3572d8;
  text-decoration: none;
}

.legal-content a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .legal-page {
    padding: 20px 12px 32px;
  }

  .legal-panel {
    padding: 30px 22px;
  }

  .legal-content {
    font-size: 14px;
    line-height: 1.85;
  }

  .legal-content h1 {
    font-size: 24px;
  }

  .legal-content h2 {
    font-size: 20px;
  }

  .legal-content h3 {
    font-size: 16px;
  }
}
</style>
